import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Pencil, X } from "lucide-react";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { invalidateContentCache } from "@/hooks/useContent";

const KNOWN_KEYS = [
  { key: "home.hero.eyebrow", title: "Homepage - Hero Eyebrow" },
  { key: "home.hero.title", title: "Homepage - Hero Heading" },
  { key: "home.hero.subtitle", title: "Homepage - Hero Subheading" },
  { key: "home.holidays_block", title: "Homepage - Holiday Schedule Section (rich text; leave blank to hide)" },
  { key: "high_holy_days.body", title: "High Holy Days Page - Full Content (rich text; leave blank to keep default schedule image)" },
  { key: "holidays.body", title: "Holidays Page - Full Content (rich text; leave blank to keep default)" },
  { key: "filmseries.body", title: "Film Series Page - Full Content (rich text; leave blank to keep default)" },
  { key: "bulletin.body", title: "Bulletin Page - Full Content (rich text; leave blank to keep default)" },
  { key: "hiring.body", title: "Hiring Page - Full Content (rich text; leave blank to keep default job description)" },
  { key: "president.message", title: "Message from the President" },
  { key: "sisterhood.body", title: "Sisterhood Page - Body" },
  { key: "mens_club.body", title: "Men's Club Page - Body" },
  { key: "social_action.body", title: "Social Action Page - Body" },
  { key: "path_to_judaism.body", title: "Path to Judaism - Body" },
];

export default function AdminContent() {
  const { authedClient } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/content");
      // Merge known keys with existing blocks so the admin always sees a full list.
      // For known keys, the KNOWN_KEYS title always wins so hint updates flow
      // through without needing a DB rewrite.
      const byKey = Object.fromEntries(data.map(b => [b.key, b]));
      const merged = KNOWN_KEYS.map(k => ({
        key: k.key,
        title: k.title,
        body: byKey[k.key]?.body || "",
      }));
      // Include any extra keys the admin created
      const extra = data.filter(b => !KNOWN_KEYS.find(k => k.key === b.key));
      setBlocks([...merged, ...extra]);
    } catch { toast.error("Could not load content blocks"); } finally { setLoading(false); }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (b) => {
    setEditingKey(b.key);
    setDraft({ title: b.title, body: b.body || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await authedClient().put(`/admin/content/${encodeURIComponent(editingKey)}`, draft);
      invalidateContentCache(editingKey);
      toast.success("Saved. Refresh the public page to see changes.");
      setEditingKey(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div data-testid="admin-content-page">
      <div className="mb-6">
        <p className="eyebrow mb-2">★ CMS</p>
        <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Page Copy</h1>
        <p className="text-sm text-[color:var(--ink-soft)] mt-1">
          Edit the words that appear on About, Message from the President, Sisterhood,
          Men&rsquo;s Club, Social Action, Path to Judaism, and the homepage hero.
        </p>
      </div>

      {editingKey && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-content-form">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl text-[color:var(--blue-ink)]">Editing: {draft.title}</h2>
              <p className="text-xs text-[color:var(--muted)] mt-1">Key: <code>{editingKey}</code></p>
            </div>
            <button type="button" onClick={() => setEditingKey(null)}><X className="w-5 h-5" /></button>
          </div>
          <RichTextEditor value={draft.body} onChange={(html) => setDraft(d => ({...d, body: html}))} />
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="content-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={() => setEditingKey(null)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-center text-[color:var(--muted)] py-8">Loading…</p> : (
        <div className="space-y-3">
          {blocks.map(b => (
            <div key={b.key} className="p-4 rounded-xl border border-[color:var(--line)] bg-white" data-testid={`admin-content-row-${b.key}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[color:var(--blue-ink)]">{b.title}</div>
                  <div className="text-xs text-[color:var(--muted)] mt-0.5"><code>{b.key}</code></div>
                  <div className="text-sm text-[color:var(--ink-soft)] mt-2 line-clamp-2" dangerouslySetInnerHTML={{ __html: b.body || "<em>(empty)</em>" }} />
                </div>
                <button onClick={() => startEdit(b)} className="btn-ghost text-sm py-2 px-3" data-testid={`content-edit-${b.key}`}>
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
